import React, { useState, useEffect } from 'react';

/**
 * Reusable lead capture modal.
 * Props:
 *  - open, onClose
 *  - title, subtitle
 *  - submitLabel (default "Enviar")
 *  - showEmail (default true), showPhone (default true)
 *  - onSubmit: async ({name, email, phone}) => any
 *  - onSubmitted: (result) => void (e.g. open WhatsApp)
 */
export default function LeadCaptureModal({
  open,
  onClose,
  title = '¿Cómo te contactamos?',
  subtitle = 'Déjanos tus datos y un asesor te contacta de inmediato.',
  submitLabel = 'Continuar a WhatsApp',
  successMessage = null,
  showWhatsAppIcon = null, // null = auto (based on submitLabel)
  showEmail = true,
  showPhone = true,
  onSubmit,
  onSubmitted,
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setSending(false);
      setError('');
      setDone(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || name.trim().length < 2) {
      setError('Ingresa tu nombre completo');
      return;
    }
    if (showPhone && !/^[\d\s+()-]{8,}$/.test(phone.trim())) {
      setError('Ingresa un teléfono válido');
      return;
    }
    if (showEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Ingresa un email válido');
      return;
    }

    setSending(true);
    try {
      const result = await onSubmit?.({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      if (successMessage) {
        // Show confirmation inside the modal instead of redirecting/closing immediately.
        setDone(true);
        setSending(false);
        onSubmitted?.(result);
      } else {
        onSubmitted?.(result);
        onClose?.();
      }
    } catch (err) {
      setError('Ocurrió un error. Inténtalo de nuevo.');
      setSending(false);
    }
  };

  const shouldShowWA = showWhatsAppIcon ?? /whatsapp/i.test(submitLabel);

  return (
    <div
      className="sm-lead-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="lead-modal"
    >
      <div className="sm-lead-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="sm-lead-modal-close"
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="lead-modal-close"
        >
          <span className="icon-font">close</span>
        </button>

        <div className="sm-lead-modal-head">
          <div className="text-label" style={{ color: 'var(--brand-accent)' }}>{done ? '¡recibido!' : 'un paso más'}</div>
          <h3 className="sm-h3" style={{ color: 'var(--brand-primary)' }}>{done ? '¡Gracias!' : title}</h3>
          {!done && subtitle && <p className="sm-lead-modal-sub">{subtitle}</p>}
        </div>

        {done ? (
          <div className="sm-lead-success" data-testid="lead-modal-success">
            <div className="sm-lead-success-icon"><span className="icon-font">check_circle</span></div>
            <p>{successMessage}</p>
            <button type="button" className="sm-lead-success-close" onClick={onClose} data-testid="lead-modal-done">
              Entendido
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="sm-lead-form">
          <label className="sm-lead-field">
            <span>Nombre completo</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Carlos Hernández"
              autoFocus
              required
              data-testid="lead-modal-name"
            />
          </label>

          {showPhone && (
            <label className="sm-lead-field">
              <span>Teléfono / WhatsApp</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="55 1234 5678"
                required
                data-testid="lead-modal-phone"
              />
            </label>
          )}

          {showEmail && (
            <label className="sm-lead-field">
              <span>Correo electrónico</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@email.com"
                required
                data-testid="lead-modal-email"
              />
            </label>
          )}

          {error && <div className="sm-lead-error">{error}</div>}

          <button
            type="submit"
            className="sm-lead-submit"
            disabled={sending}
            data-testid="lead-modal-submit"
          >
            {sending ? (
              <>Enviando…</>
            ) : (
              <>
                {shouldShowWA && <img loading="lazy" src="/images/whatsapp.svg" alt="" aria-hidden="true" />}
                {submitLabel}
                <span className="icon-font">arrow_forward</span>
              </>
            )}
          </button>

          <p className="sm-lead-privacy">
            Al continuar aceptas que te contactemos por teléfono, WhatsApp o correo.
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
